<template>
  <div
    @click="emit('updateOnboardingStep', { value: props.currentStep - 1 })"
    class="cursor-pointer hover:scale-105 duration-300 rounded-md bg-gray-800 px-3 py-2.5 text-text-primary flex justify-center items-center text-xl absolute top-8 left-4 lg:left-16"
  >
    <img class="w-4 mr-1" src="../../assets/icons/leftarrow.svg" :alt="$t('common.back')" />
    <h1 class="text-xs text-text-primary">{{ $t('common.back') }}</h1>
  </div>
  <div
    @click="emit('updateOnboardingStep', { value: props.currentStep + 1 })"
    class="cursor-pointer hover:scale-105 duration-300 rounded-md bg-gray-800 px-3 py-2.5 text-text-primary flex justify-center items-center text-xl absolute top-8 right-4 lg:right-16"
  >
    <h1 class="text-xs text-text-primary">{{ $t('common.skip') }}</h1>
    <img class="w-4 ml-1" src="../../assets/icons/rightarrow.svg" :alt="$t('common.skip')" />
  </div>
  <div
    v-if="loading"
    class="w-full px-4 lg:px-16 flex flex-col items-center justify-center mt-16 lg:mt-24 py-24"
  >
    <p class="text-text-secondary">{{ $t('common.loading') }}</p>
  </div>
  <div
    v-else-if="campaign"
    class="w-full px-4 lg:px-16 flex flex-col items-center mt-16 lg:mt-24 gap-3 pb-8"
  >
    <TrialOfferCard
      :campaign="campaign"
      :tier-label="tierLabel"
      :is-player-tier="isPlayerTier"
      :tier="tier"
      :submitting="submitting"
      :show-secondary-action="true"
      @submit="continueAndSubmit"
      @secondary-action="subscribeInstead"
    />
  </div>
  <AlertPopup />
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { onMounted } from 'vue';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useGtm } from '@gtm-support/vue-gtm';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import AlertPopup from '../AlertPopup.vue';
import TrialOfferCard from '../TrialOfferCard.vue';
import { useTrialCampaign } from '../../composables/useTrialCampaign';

const { t } = useI18n();
const auth = useAuth();
const gtm = useGtm();
const route = useRoute();
const store = useStore();

const emit = defineEmits(['updateOnboardingStep', 'updateOverwriteTrialCookie']);
const props = defineProps({
  currentStep: Number,
});

const {
  campaign,
  loading,
  submitting,
  isPlayerTier,
  tierLabel,
  tier,
  fetchCampaign,
  startTrial,
  getCampaignErrorMessage,
  getStartTrialErrorMessage,
} = useTrialCampaign();

function getTrialCookieValue() {
  if (typeof route.params.source === 'string' && route.params.source.trim()) {
    return route.params.source.trim();
  }

  const value = document.cookie
    .split('; ')
    .find((row) => row.startsWith('trial='))
    ?.split('=')[1];

  if (!value || value === 'true') return null;

  return decodeURIComponent(value);
}

function clearTrialCookie() {
  document.cookie = 'trial=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';

  if (window.location.hostname.endsWith('refrag.gg')) {
    document.cookie = 'trial=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.refrag.gg';
  }
}

function getCampaignName() {
  if (typeof route.query.source === 'string' && route.query.source.trim()) {
    return route.query.source.trim();
  }

  return getTrialCookieValue();
}

function subscribeInstead() {
  clearTrialCookie();
  emit('updateOverwriteTrialCookie', true);
}

onMounted(async () => {
  const campaignName = getCampaignName();

  if (!campaignName) {
    subscribeInstead();
    loading.value = false;
    return;
  }

  try {
    await fetchCampaign(campaignName);
  } catch (err) {
    store.commit('showMessage', {
      messageType: 'error',
      messageText: getCampaignErrorMessage(err),
    });
    subscribeInstead();
  } finally {
    loading.value = false;
  }
});

async function continueAndSubmit() {
  const campaignName = getCampaignName();

  if (!campaignName || !campaign.value) return;

  submitting.value = true;

  try {
    await startTrial(campaignName);

    clearTrialCookie();
    store.commit('showMessage', {
      messageType: 'success',
      messageText: t('onboarding.free_trial.started'),
    });
    gtm?.push({ event: 'trial_started' });

    await auth.fetch();
    emit('updateOnboardingStep', { value: props.currentStep + 1 });
  } catch (err) {
    store.commit('showMessage', {
      messageType: 'error',
      messageText: getStartTrialErrorMessage(err),
    });
    subscribeInstead();
  } finally {
    submitting.value = false;
  }
}
</script>
